import cloneDeep from "lodash/cloneDeep";
import { CableNaming, UTP2Groups, UTP2Substations } from "../../shared/enums/UTP2";
import type { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";
import type { HvSurveyableAsset, WorkflowV2 } from "../../shared/interfaces/WorkflowV2";
import { buildConditionsList, findSurveyMetadataCustomQuestionGroups, findSurveyMetadataField } from "./metadata";
import { rebuildMeasurementsOrder } from "./measurementOrdering";
import {
  assetToOrderedMeasurements,
  assetToGroupedMeasures,
  groupedMeasurementsToHvSurveyableAssets,
  groupedToAssetMeasures,
  hvSurveyableAssetToGroupedMeasurement,
  hvSurveyableAssetToOrderedMeasurement,
} from "./measurementTransforms";
import type { GroupedMeasurement, WorkflowState, WorkflowType } from "./types";

type WorkflowDetailOverrides = Partial<WorkflowV1["workflowDetail"]> | Partial<WorkflowV2["workflowDetail"]>;
interface GenerateWorkflowStateOptions {
  preserveStoredMeasurementsOrder?: boolean;
}

export function generateWorkflowState(
  workflow: WorkflowV1 | WorkflowV2,
  { preserveStoredMeasurementsOrder = true }: GenerateWorkflowStateOptions = {},
): WorkflowState {
  if (workflow.workflowDetail && workflow.workflowDetail.version === 1) {
    return {
      workflow: cloneDeep(workflow),
      cableNaming: findSurveyMetadataField(workflow, UTP2Groups.Substation, UTP2Substations.CableNaming)
        ?.value as CableNaming,
      conditions: buildConditionsList(workflow),
      customQuestionsGroups: findSurveyMetadataCustomQuestionGroups(workflow),
      groupedMeasurements: assetToGroupedMeasures(workflow.workflowDetail?.assets),
      workflowType: workflow.workflowDetail?.substation_id ? "substation" : "generic",
      orderedMeasurements: assetToOrderedMeasurements(workflow.workflowDetail?.assets),
      duplicateMeasurementGroupIds: [],
    };
  }

  const workflowType = workflow.workflowDetail?.substation_id ? "substation" : "generic";
  const groupedMeasurements = hvSurveyableAssetToGroupedMeasurement(workflow.workflowDetail?.assets);

  const isCableNaming = (value: string | undefined): value is CableNaming =>
    value !== undefined && Object.values(CableNaming).includes(value as CableNaming);

  const cableNamingValue = findSurveyMetadataField(workflow, UTP2Groups.Substation, UTP2Substations.CableNaming)?.value;

  const cableNaming = isCableNaming(cableNamingValue) ? cableNamingValue : undefined;

  if (!preserveStoredMeasurementsOrder) {
    const orderedMeasurements = hvSurveyableAssetToOrderedMeasurement(workflow.workflowDetail?.assets).sort(
      (left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER),
    );

    return {
      workflow: cloneDeep(workflow),
      cableNaming: cableNaming,
      conditions: buildConditionsList(workflow),
      customQuestionsGroups: findSurveyMetadataCustomQuestionGroups(workflow),
      groupedMeasurements,
      workflowType,
      orderedMeasurements,
      duplicateMeasurementGroupIds: [],
    };
  }

  const [orderedGroupedMeasurements, orderedMeasurements] = rebuildMeasurementsOrder(groupedMeasurements);
  const normalisedWorkflow = buildWorkflowWithMeasurements(
    cloneDeep(workflow),
    orderedGroupedMeasurements,
    workflowType,
  );

  return {
    workflow: normalisedWorkflow,
    cableNaming: cableNaming,
    conditions: buildConditionsList(workflow),
    customQuestionsGroups: findSurveyMetadataCustomQuestionGroups(workflow),
    groupedMeasurements: orderedGroupedMeasurements,
    workflowType,
    orderedMeasurements,
    duplicateMeasurementGroupIds: [],
  };
}

export function buildWorkflowWithMeasurements(
  workflow: WorkflowV1 | WorkflowV2,
  groupedMeasurements: GroupedMeasurement[],
  workflowType: WorkflowType | undefined,
  detailOverrides: WorkflowDetailOverrides = {},
  transformV2Assets?: (assets: HvSurveyableAsset[]) => HvSurveyableAsset[],
): WorkflowV1 | WorkflowV2 {
  if (isWorkflowV1(workflow)) {
    return {
      ...workflow,
      workflowDetail: {
        ...workflow.workflowDetail,
        ...(detailOverrides as Partial<WorkflowV1["workflowDetail"]>),
        assets: groupedToAssetMeasures(groupedMeasurements),
      },
    };
  }

  const nextAssets = groupedMeasurementsToHvSurveyableAssets(groupedMeasurements, workflowType);
  const assets = transformV2Assets ? transformV2Assets(nextAssets) : nextAssets;
  const detailOverridesV2 = detailOverrides as Partial<WorkflowV2["workflowDetail"]>;
  return {
    ...workflow,
    workflowDetail: {
      ...workflow.workflowDetail,
      ...detailOverridesV2,
      assets,
      asset_types: detailOverridesV2.asset_types ?? workflow.workflowDetail.asset_types,
      version: 2,
    },
  };
}

function isWorkflowV1(workflow: WorkflowV1 | WorkflowV2): workflow is WorkflowV1 {
  return Boolean(workflow.workflowDetail && workflow.workflowDetail.version === 1);
}
